// src/components/Upcoming.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import styles from "../css/Upcoming.module.css";
import Footer from "./Footer";

const fallbackLogo = "/images/logo-placeholder.png";

const LeagueGroup = ({
  league,
  matches,
  userTimeZone,
  follows,
  onFollowChange,
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry], o) => {
        if (entry.isIntersecting) {
          setVisible(true);
          o.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.leagueGroup}>
      <div className={styles.leagueHeader}>
        <img
          src={league.logo}
          alt={league.name}
          className={styles.leagueLogo}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = fallbackLogo;
          }}
        />
        <span className={styles.leagueName}>{league.name}</span>
      </div>

      {visible ? (
        <ul className={styles.list}>
          {matches.map((match) => {
            const { home, away } = match.teams;
            // figure out which team (if any) is followed
            const selected = follows.includes(home.id)
              ? home.id.toString()
              : follows.includes(away.id)
              ? away.id.toString()
              : "";

            return (
              <li key={match.fixture.id} className={styles.matchItem}>
                {/* Date/Time */}
                <div className={styles.date}>
                  {new Date(match.fixture.date).toLocaleString(undefined, {
                    timeZone: userTimeZone,
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>

                {/* Score + Follow row */}
                <div className={styles.infoRow}>
                  <div className={styles.scoreLine}>
                    <div className={styles.teamLeft}>
                      <img
                        src={home.logo}
                        alt={home.name}
                        className={styles.teamLogo}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackLogo;
                        }}
                      />
                      <span className={styles.teamName}>{home.name}</span>
                    </div>

                    <div className={styles.scoreMiddle}>-</div>

                    <div className={styles.teamRight}>
                      <span className={styles.teamName}>{away.name}</span>
                      <img
                        src={away.logo}
                        alt={away.name}
                        className={styles.teamLogo}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackLogo;
                        }}
                      />
                    </div>
                  </div>

                  {/* Follow dropdown */}
                  <select
                    className={styles.followSelect}
                    value={selected}
                    onChange={(e) => onFollowChange(Number(e.target.value))}
                    aria-label="Follow team"
                  >
                    <option value="">Follow Team</option>
                    <option value={home.id}>{home.name}</option>
                    <option value={away.id}>{away.name}</option>
                  </select>
                </div>

                {/* Status underneath */}
                <div className={styles.status} style={{ marginTop: "10px" }}>
                  Status: {match.fixture.status.long}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={styles.placeholder}>Loading fixtures…</div>
      )}
    </div>
  );
};

export default function Upcoming() {
  const [matches, setMatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [follows, setFollows] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("followedTeams")) || [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // fetch upcoming fixtures
  useEffect(() => {
    let mounted = true;
    axios
      .get("https://fetchrss.visiontrek.io/api-football/upcoming-fixtures")
      .then((resp) => {
        if (mounted) setMatches(resp.data.response || []);
      })
      .catch((err) => {
        if (mounted) setError(err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // handle team follow/unfollow
  const handleFollowChange = (teamId) => {
    setFollows((prev) => {
      let updated;
      if (teamId === 0 || !teamId) {
        // if blank selected, do nothing
        updated = prev;
      } else if (prev.includes(teamId)) {
        updated = prev.filter((id) => id !== teamId);
      } else {
        updated = [...prev, teamId];
      }
      localStorage.setItem("followedTeams", JSON.stringify(updated));
      return updated;
    });
  };

  if (loading)
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  if (error) return <div className={styles.error}>Error: {error.message}</div>;

  // Filter by search
  const filtered = matches.filter((m) => {
    const t = searchTerm.toLowerCase();
    return (
      m.league.name.toLowerCase().includes(t) ||
      m.teams.home.name.toLowerCase().includes(t) ||
      m.teams.away.name.toLowerCase().includes(t)
    );
  });

  // Group by league
  const grouped = filtered.reduce((acc, m) => {
    const id = m.league.id;
    if (!acc[id]) acc[id] = { league: m.league, matches: [] };
    acc[id].matches.push(m);
    return acc;
  }, {});

  // Sort today's fixtures first
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  Object.values(grouped).forEach((g) => {
    g.matches.sort((a, b) => {
      const da = new Date(a.fixture.date),
        db = new Date(b.fixture.date);
      const aT = da >= todayStart && da < todayStart.getTime() + 86400000,
        bT = db >= todayStart && db < todayStart.getTime() + 86400000;
      if (aT && !bT) return -1;
      if (!aT && bT) return 1;
      return da - db;
    });
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchWrapper}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by league or team"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className={styles.clearButton}
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {Object.values(grouped).map(({ league, matches }) => (
        <LeagueGroup
          key={league.id}
          league={league}
          matches={matches}
          userTimeZone={userTimeZone}
          follows={follows}
          onFollowChange={handleFollowChange}
        />
      ))}

      <Footer />
    </div>
  );
}
